import React, { useState } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import { Page } from '../../../../layout';
import PageInstructions from '../../../../shared/components/PageInstructions';
import { FormGroup, FormControlLabel } from '@mui/material';
import MuiSwitch from '../../../../shared/components/MuiSwitch';
import { Navigate, useParams } from 'react-router-dom';
import PerformanceMetricPageEditViewControls from '../PerformanceMetricPageEditViewControls';
import ApolloError from '../../../../shared/components/ApolloError';
import { Breadcrumb } from '../../../../shared/components/breadcrumb/Breadcrumb';
import { getPageTitle } from '../../../../shared/services/pageTitleService';
import GET_ORG_UNIT_INFO from '../../../../shared/queries/getOrgUnitInfoQuery';
import { metricProperties } from '../pageSettings/pageSettingsByPageMode';
import GET_METRIC_INFO from '../queries/getMetricInfo';
import DataPointMapper from '../../mappers/dataPointMapper';
import { getDataNotFoundPage } from '../../../../shared/services/getDataNotFoundPageService';
import { userTier } from '../../../../../apollo/cache';
import LinkButton from '../../../../shared/components/LinkButton';
import { canUserAccessSeveralOrganization } from '../../../../shared/services/userAccessibilityService';
import OrgUnitBreadcrumbAnnotation from '../../../../shared/components/breadcrumb/annotations/OrgUnitBreadcrumbAnnotation';

const ViewPerformanceMetric = () => {
  const { orgUnit, appraisal, checkPoint, metric } = useParams();
  const orgUnitId = parseInt(orgUnit);
  const appraisalId = parseInt(appraisal);
  const checkPointId = parseInt(checkPoint);
  const metricId = parseInt(metric);

  const pageSettings = getPageSettings();
  const previousLink = `/check-points/${orgUnitId}/view-check-point/${appraisalId}/${checkPointId}`;

  const tier = useReactiveVar(userTier);

  const [showDescriptions, setShowDescriptions] = useState(false);
  const [selectedMetricProperty, setSelectedMetricProperty] = useState(null);

  const {
    data,
    error: performanceMetricError,
    loading: performanceMetricLoading
  } = useQuery(GET_METRIC_INFO, {
    variables: { metricId: metricId, checkPointId: checkPointId, appraisalId: appraisalId }
  });
  const performanceMetric =
    data &&
    data.performanceMetric &&
    data.performanceMetric.CheckpointPerformanceItemDataPoints.length > 0
      ? DataPointMapper.toViewPage(data.performanceMetric.CheckpointPerformanceItemDataPoints[0])
      : null;

  const {
    data: orgUnitData,
    error: orgUnitError,
    loading: orgUnitLoading
  } = useQuery(GET_ORG_UNIT_INFO, {
    variables: { id: orgUnitId }
  });

  const orgUnitName = orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.Name;
  const orgName =
    orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.MddapOrganization.Name;
  const renderedOrgUnit = orgUnitData && (
    <OrgUnitBreadcrumbAnnotation orgUnit={orgUnitData.mddapOrgUnit} key={orgUnitName} />
  );

  const pageTitle = getPageTitle([orgUnitName, 'View Performance Report Metric']);
  const commonItems = [renderedOrgUnit, `Appraisal ${appraisalId}`];
  const annotationItems = canUserAccessSeveralOrganization(tier)
    ? [orgName, ...commonItems]
    : commonItems;

  const breadcrumbPathItems = [
    { name: 'Check Points', url: `/check-points/${orgUnitId}` },
    {
      name: 'View Check Point',
      url: previousLink
    },
    { name: 'View Performance Report Metric', url: '' }
  ];

  const handleClickOutside = () => {
    setSelectedMetricProperty(null);
  };

  const handleOnClickMetricField = clickedField => {
    const metricProperty = pageSettings.metricProperties.find(x => x.field === clickedField);
    setSelectedMetricProperty(metricProperty);
  };

  if (
    (orgUnitData && orgUnitData.mddapOrgUnit === null) ||
    (data &&
      (!data.performanceMetric ||
        !data.performanceMetric.CheckpointPerformanceItemDataPoints.length))
  ) {
    return <Navigate to={getDataNotFoundPage(tier)} />;
  }

  if (orgUnitError || performanceMetricError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='performance-report-metric-page'>
      <Breadcrumb
        pathItems={breadcrumbPathItems}
        annotationItems={annotationItems}
        loading={orgUnitLoading}
      />

      <div className='performance-report-metric-page__container' onClick={handleClickOutside}>
        <PerformanceMetricPageEditViewControls
          performanceMetric={performanceMetric}
          handleOnClickMetricField={handleOnClickMetricField}
          selectedMetricProperty={selectedMetricProperty}
          showDescriptions={showDescriptions}
          pageSettings={pageSettings}
          loading={performanceMetricLoading}
        />

        <div className='sticky-section'>
          <div className='instructions'>
            <div className='action-button-group'>
              <div className='action-buttons'>
                <LinkButton
                  data-testid='cancel-button'
                  className='secondary-button'
                  type='button'
                  to={previousLink}>
                  Back
                </LinkButton>
              </div>
            </div>
            <PageInstructions
              title='View Performance Report Metric'
              description={pageSettings.description}
            />
          </div>
          <FormGroup className='switch-container'>
            <FormControlLabel
              key='FieldDescriptions'
              labelPlacement='start'
              label='Field Descriptions'
              className='switch-label'
              control={
                <MuiSwitch
                  id='FieldDescriptions'
                  checked={showDescriptions}
                  onChange={(_, checked) => setShowDescriptions(checked)}
                />
              }
            />
          </FormGroup>
        </div>
      </div>
    </Page>
  );
};

const getPageSettings = () => {
  return {
    firstSectionTitle: 'Metric Information',
    secondSectionTitle: 'Additional Metric Information',
    description: (
      <>
        Submitted metric fields are displayed first and un-editable metric fields are displayed at
        the bottom of the page, these can be viewed by clicking the “+” button. Field descriptions
        hidden by default and can be shown for reference. Click &quot;Back&quot; to return to the
        View Check Point page.
      </>
    ),
    viewOnlyMode: true,
    metricProperties: metricProperties
  };
};

export default ViewPerformanceMetric;
